import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, delay, filter, Observable, switchMap, take, tap } from 'rxjs';

import { StorageService } from '../services/storage.service';
import { AuthService } from './auth.service';
import {
  decodeAuthToken,
  FORCE_REFRESH_JWT_TOKEN_CONTEXT,
  SKIP_AUTH_HEADER_CONTEXT,
  SKIP_REFRESH_JWT_TOKEN_INTERCEPTOR_HEADER_KEY,
} from './jwt-token.utils';
import { IdleTimeoutService } from '../services/idle-timeout.service';

/**
 * Handle refreshing token after expiring
 */
export const jwtInterceptor: HttpInterceptorFn = (req, next) => inject(JwtRefreshService).intercept(req, next);

/** Stav obnovy tokenu je sdílený mezi požadavky, proto je logika ve službě a interceptor ji jen volá. */
@Injectable({ providedIn: 'root' })
export class JwtRefreshService {
  headerName = 'Authorization';
  authScheme = 'Bearer ';

  private _authService = inject(AuthService);
  private _storageService = inject(StorageService);
  private _idleTimeoutService = inject(IdleTimeoutService);
  private _jwtPending = false;
  private _continueWithRequest$ = new BehaviorSubject<string | null>('-1');

  /**
   * Kontroluje zda je uživatel přihlášen (má IS_LOGGED_IN v sessionStorage).
   * IdleTimeoutService by se měl startovat pouze pro přihlášené uživatele.
   */
  private _isUserLoggedIn(): boolean {
    return this._authService.isLoggedIn();
  }

  handleInterception(token: string | null, request: HttpRequest<unknown>, next: HttpHandlerFn) {
    if (request.headers.get(SKIP_REFRESH_JWT_TOKEN_INTERCEPTOR_HEADER_KEY)) return next(request);

    const forceRefreshToken = request.context.get(FORCE_REFRESH_JWT_TOKEN_CONTEXT) === true;

    if (!this._jwtPending && (this._tokenAboutToExpire(token) || forceRefreshToken)) {
      this._jwtPending = true;
      this._continueWithRequest$.next(null);

      return this._authService.retrieveAndSaveAuthToken(true, true).pipe(
        delay(1000),
        switchMap((authResponse) =>
          next(
            request.clone({
              setHeaders: {
                [this.headerName]: `${this.authScheme}${authResponse.accessToken}`,
              },
            })
          ).pipe(
            tap(() => {
              this._jwtPending = false;

              this._continueWithRequest$.next(authResponse.accessToken);
            })
          )
        )
      ) as Observable<HttpEvent<unknown>>;
    }

    if (!token) return next(request);

    if (!this._tokenAboutToExpire(token)) this._continueWithRequest$.next(token);

    return this._continueWithRequest$.pipe(
      filter(Boolean),
      take(1),
      tap((newToken) => {
        if (newToken === '-1') return;

        // Startovat IdleTimeoutService pouze pokud je uživatel přihlášen
        // (má IS_LOGGED_IN v sessionStorage). Zabraňuje spuštění timeoutu
        // pro nepřihlášené uživatele se starým tokenem v localStorage.
        if (this._isUserLoggedIn()) {
          this._idleTimeoutService.startInactivityTimer();
        }

        request = request.clone({
          setHeaders: {
            [this.headerName]: `${this.authScheme}${newToken}`,
          },
        });
      }),
      switchMap(() => next(request))
    );
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    // ✅ Pokud kontext říká přeskočit Authorization hlavičku
    const skipAuth = request.context.get(SKIP_AUTH_HEADER_CONTEXT);
    if (skipAuth) {
      return next(request); // neaplikovat token
    } else {
      return this.handleInterception(this._storageService.getToken(), request, next);
    }
  }

  private _tokenAboutToExpire(token: string | null) {
    if (token) {
      const authData = decodeAuthToken(token);
      const now = Math.floor(new Date().getTime() / 1_000);
      const time = authData.exp - now;
      return time < 2 * 60;
    }

    return false;
  }
}
